import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowRight, Plus } from 'lucide-react';
import { adminQueries } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatNumber, hasPermission, labelOf, METER_SOURCE_LABELS } from '@blimo/shared';
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, DataTable, ErrorState, StatCard } from '@blimo/ui';
import { DEFAULT_PAGE_SIZE } from '../../lib/list-search.js';
import { checkColumns } from '../document-checks/list.jsx';
import { DocumentCheckCreateSheet } from '../document-checks/create-sheet.jsx';
import { announcementColumns } from '../communication/announcements/list.jsx';
import { threadColumns } from '../communication/inbox/list.jsx';
import { proxyColumns } from '../owner-meetings/proxies.jsx';
import { currentYear, meterLabel } from '../meters/utils.js';

/*
 * Liegenschaft › Zähler / Belegprüfungen / Kommunikation: kompakte, auf die Liegenschaft gefilterte Ausschnitte der
 * jeweiligen Fachseiten (gleiche API, `filter[property_id]`) mit Sprung in die vollständige Ansicht.
 */

/** Spalte „Liegenschaft“ ist im Liegenschafts-Kontext überflüssig. */
const withoutProperty = (columns) => columns.filter((c) => !['property', 'properties'].includes(c.id));

/** Seitenweise Liste je Abschnitt (eigener Seitenzustand, nicht in der URL). */
function usePagedQuery(factory, filter, pageSize = DEFAULT_PAGE_SIZE) {
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize });
  const query = useQuery(factory({ page: pagination.pageIndex + 1, per_page: pagination.pageSize, filter }));
  return { ...query, pagination, setPagination };
}

function Section({ title, description, actions, children }) {
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <CardTitle as="h2">{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

/** Link-Button zur vollständigen Fachseite, vorgefiltert auf die Liegenschaft (Zahl → saubere URL `?property=5`). */
function AllLink({ to, search, children }) {
  return (
    <Button asChild size="sm" variant="outline">
      <Link to={to} search={search}>
        {children} <ArrowRight aria-hidden="true" />
      </Link>
    </Button>
  );
}

function PagedTable({ label, query, columns, emptyTitle, emptyDescription }) {
  if (query.error) return <ErrorState compact error={query.error} onRetry={() => query.refetch()} />;
  return (
    <DataTable
      label={label}
      columns={columns}
      data={query.data?.data}
      rowCount={query.data?.meta?.total}
      isLoading={query.isPending}
      isFetching={query.isFetching}
      getRowId={(row) => String(row.id)}
      pagination={query.pagination}
      onPaginationChange={query.setPagination}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
    />
  );
}

/* ------------------------------------------------------------------ Zähler */

/** @param {{ propertyId: number|string }} props */
export function PropertyMetersTab({ propertyId }) {
  const api = useApi();
  const year = currentYear();
  const property = Number(propertyId);
  const meters = usePagedQuery((params) => adminQueries.meters(api, params), { property_id: propertyId });
  const readings = useQuery(adminQueries.meterReadings(api, year, { per_page: 1, filter: { property_id: propertyId } }));
  const summary = readings.data?.meta?.summary;
  const columns = useMemo(
    () => [
      {
        id: 'number',
        header: 'Zähler',
        cell: ({ row }) => (
          <span className="flex flex-wrap items-center gap-1.5">
            <Link to="/zaehler/$meterId" params={{ meterId: String(row.original.id) }} className="font-medium text-primary hover:underline">
              {meterLabel(row.original)}
            </Link>
            {row.original.archived_at && <Badge variant="neutral">Archiviert</Badge>}
          </span>
        ),
      },
      { id: 'contract_number', header: 'Vertrag', cell: ({ row }) => row.original.contract_number ?? '–' },
      { id: 'supplier', header: 'Versorger', cell: ({ row }) => row.original.supplier?.name ?? '–' },
      { id: 'reader', header: 'Ableser', cell: ({ row }) => row.original.reader?.name ?? '–' },
      { id: 'source', header: 'Quelle', cell: ({ row }) => labelOf(METER_SOURCE_LABELS, row.original.source) },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-4">
      <Section
        title={`Ablesung ${year}`}
        description="Stand der Zählerablesung dieser Liegenschaft im laufenden Jahr."
        actions={
          <AllLink to="/zaehler/staende" search={{ year, property }}>
            Zählerstände {year}
          </AllLink>
        }
      >
        {readings.error ? (
          <ErrorState compact error={readings.error} onRetry={() => readings.refetch()} />
        ) : summary && summary.total === 0 ? (
          <p className="text-sm text-muted-foreground">Für {year} ist für diese Liegenschaft noch kein Ablesejahr angelegt.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard label="Offen" value={summary ? formatNumber(summary.open) : '…'} />
            <StatCard label="Abgelesen" value={summary ? formatNumber(summary.read) : '…'} />
            <StatCard label="Ohne Ablesung" value={summary ? formatNumber(summary.closed) : '…'} />
            <StatCard label="Gesamt" value={summary ? formatNumber(summary.total) : '…'} />
          </div>
        )}
      </Section>
      <Section
        title="Zähler"
        description="Zähler und Versorger der Liegenschaft (Stammdaten aus Cobra)."
        actions={
          <AllLink to="/zaehler" search={{ property }}>
            Alle Zähler
          </AllLink>
        }
      >
        <PagedTable
          label="Zähler der Liegenschaft"
          query={meters}
          columns={columns}
          emptyTitle="Keine Zähler"
          emptyDescription="Für diese Liegenschaft sind keine Zähler erfasst."
        />
      </Section>
    </div>
  );
}

/* ------------------------------------------------------------------ Belegprüfungen */

/** @param {{ propertyId: number|string }} props */
export function PropertyDocumentChecksTab({ propertyId }) {
  const api = useApi();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const property = Number(propertyId);
  const checks = usePagedQuery((params) => adminQueries.documentChecks(api, params), { property_id: propertyId });
  const columns = useMemo(() => withoutProperty(checkColumns()), []);

  return (
    <div className="flex flex-col gap-4">
      <Section
        title="Belegprüfungen"
        description="Prüfungen je Abrechnungsjahr mit Kassenprüfern, Bestätigungen und Versand an die Eigentümer."
        actions={
          <>
            {hasPermission(me, 'document-checks.manage') && (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus aria-hidden="true" /> Belegprüfung anlegen
              </Button>
            )}
            {hasPermission(me, 'comments.moderate') && (
              <AllLink to="/kommunikation/kommentare" search={{ property }}>
                Kommentare
              </AllLink>
            )}
          </>
        }
      >
        <PagedTable
          label="Belegprüfungen der Liegenschaft"
          query={checks}
          columns={columns}
          emptyTitle="Keine Belegprüfungen"
          emptyDescription="Für diese Liegenschaft wurde noch keine Belegprüfung angelegt."
        />
      </Section>
      <DocumentCheckCreateSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        defaultPropertyId={String(propertyId)}
        onCreated={(check) => navigate({ to: '/belegpruefung/$checkId', params: { checkId: String(check.id) } })}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ Kommunikation */

/** @param {{ propertyId: number|string }} props */
export function PropertyCommunicationTab({ propertyId }) {
  const api = useApi();
  const { data: me } = useMe();
  const property = Number(propertyId);
  const filter = { property_id: propertyId };
  const canAnnouncements = hasPermission(me, 'announcements.view');
  const canMessages = hasPermission(me, 'messages.view');
  const canProxies = hasPermission(me, 'proxy-authorizations.view');
  const announcements = usePagedQuery((params) => ({ ...adminQueries.announcements(api, params), enabled: canAnnouncements }), filter);
  const threads = usePagedQuery((params) => ({ ...adminQueries.messages(api, params), enabled: canMessages }), filter);
  const proxies = usePagedQuery((params) => ({ ...adminQueries.proxyAuthorizations(api, params), enabled: canProxies }), filter);
  const announcementCols = useMemo(() => withoutProperty(announcementColumns()), []);
  const threadCols = useMemo(() => threadColumns(), []);
  const proxyCols = useMemo(() => withoutProperty(proxyColumns(api)), [api]);

  if (!canAnnouncements && !canMessages && !canProxies) {
    return <p className="text-sm text-muted-foreground">Keine Berechtigung für Mitteilungen, Nachrichten oder Vollmachten.</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      {canAnnouncements && (
        <Section
          title="Mitteilungen"
          description="Aushänge und Rundschreiben an die Eigentümer dieser Liegenschaft."
          actions={
            <>
              {hasPermission(me, 'announcements.manage') && (
                <Button asChild size="sm">
                  <Link to="/kommunikation/mitteilungen/neu" search={{ property }}>
                    <Plus aria-hidden="true" /> Mitteilung erstellen
                  </Link>
                </Button>
              )}
              <AllLink to="/kommunikation/mitteilungen" search={{ property }}>
                Alle Mitteilungen
              </AllLink>
            </>
          }
        >
          <PagedTable
            label="Mitteilungen der Liegenschaft"
            query={announcements}
            columns={announcementCols}
            emptyTitle="Keine Mitteilungen"
            emptyDescription="Für diese Liegenschaft gibt es noch keine Mitteilungen."
          />
        </Section>
      )}
      {canMessages && (
        <Section
          title="Nachrichten"
          description="Anfragen der Eigentümer an die Verwaltung zu dieser Liegenschaft."
          actions={
            <AllLink to="/kommunikation/posteingang" search={{ property }}>
              Posteingang
            </AllLink>
          }
        >
          <PagedTable
            label="Nachrichten der Liegenschaft"
            query={threads}
            columns={threadCols}
            emptyTitle="Keine Nachrichten"
            emptyDescription="Zu dieser Liegenschaft liegen keine Nachrichten vor."
          />
        </Section>
      )}
      {canProxies && (
        <Section
          title="Stimmrechtsvollmachten"
          description="Vollmachten für Eigentümerversammlungen dieser Liegenschaft."
          actions={
            <AllLink to="/eigentuemerversammlung/vollmachten" search={{ property }}>
              Alle Vollmachten
            </AllLink>
          }
        >
          <PagedTable
            label="Vollmachten der Liegenschaft"
            query={proxies}
            columns={proxyCols}
            emptyTitle="Keine Vollmachten"
            emptyDescription="Für diese Liegenschaft wurden keine Vollmachten erteilt."
          />
        </Section>
      )}
    </div>
  );
}
